import { FC, useEffect, useState } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import db from 'mobile_web/modules/db'
import { AppManifest, RootStoreState } from 'mytours-read-common'
import { getDirMode } from '../../platform'
import { IonGrid, IonRow } from '@ionic/react'
import CardImage from 'mobile_web/modules/common/components/card-image'
import { FavouritedItem } from '../types'
import { generateImageUrl, getScreenSize } from 'common_lib/utils'
import { ImageSizePreset, ImageSizePresetMap, ScreenSize } from 'common_lib/types'
import { onLinkAction } from 'mobile_web/modules/screen/functions'
import { useSelector } from 'react-redux'
import { removeFromFavouritedKeys } from '../queries'
import strings from '../../../strings'
import { DexieFavouritedItem } from 'mobile_web/modules/db/types'

interface FavouriteGroupBlockProps {
	appManifest: AppManifest
}

const FavouriteGroupBlock: FC<FavouriteGroupBlockProps> = ({ appManifest }) => {
	const appMetaData = useSelector((state: RootStoreState) => state.settings.appMetaData)
	const favouritedItems = useLiveQuery(() =>
		db.favouritedItems.toArray().then(items => {
			const dedupedItems = items.reduce((acc, item) => {
				const existingItem = acc.find(i => `${i.item_id}-${i.item_type}` === `${item.item_id}-${item.item_type}`)
				if (existingItem) {
					return acc
				}
				return [...acc, item]
			}, [] as DexieFavouritedItem[])
			return dedupedItems
		}),
	)
	const [screenSize, setScreenSize] = useState<ScreenSize>(getScreenSize())

	useEffect(() => {
		window.addEventListener('resize', updateScreenSize)
		return () => {
			window.removeEventListener('resize', updateScreenSize)
		}
	}, [])

	const updateScreenSize = (): void => {
		const newScreenSize = getScreenSize()
		if (newScreenSize !== screenSize) {
			setScreenSize(newScreenSize)
		}
	}

	const onItemClicked = (item: FavouritedItem) => () => {
		if (item.item_type === 'collection') {
			const collection = appManifest.collections.find(c => c.id === item.item_id)
			const version = collection.version
			onLinkAction(`${item.item_type}_${item.item_subtype}`, item.item_id, version, () => {})
		} else if (item.item_type === 'screen') {
			const screen = appManifest.screens.find(s => s.id === item.item_id)
			const version = screen.version
			onLinkAction(`${item.item_type}_${item.item_subtype}`, item.item_id, version, () => {})
		}
	}

	const getImageSize = () => {
		const imageSizePreset = ImageSizePreset.Grid

		return {
			imageSizePreset,
			...ImageSizePresetMap[imageSizePreset],
		}
	}

	return true ? (
		<>
			<IonGrid dir={getDirMode()} className="block favourite-group-block">
				<IonRow dir={getDirMode()} className="block-header">
					<h2 className="subheading">{strings.favourites}</h2>
				</IonRow>
				<IonRow className="block-content">
					{favouritedItems?.map((favourite, index) => {
						const size = getImageSize()
						const toggleFavorite: React.MouseEventHandler<HTMLButtonElement> = e => {
							e.stopPropagation()
							const key = `${favourite.item_id}-${favourite.item_type}`
							removeFromFavouritedKeys(key)
						}

						const coverImageGridFocalPoint = favourite.cover_image_grid?.file?.focal_point
						const coverImageFocalPoint = favourite.cover_image?.file?.focal_point
						const coverImageGridCrop =
							coverImageGridFocalPoint && (coverImageGridFocalPoint[0] !== 0.5 || coverImageGridFocalPoint[1] !== 0.5)
						const coverImageCrop =
							coverImageFocalPoint && (coverImageFocalPoint[0] !== 0.5 || coverImageFocalPoint[1] !== 0.5)

						const coverImageGridOptions = {
							crop: coverImageGridCrop,
							focalPoint: coverImageGridFocalPoint,
						}

						const coverImageOptions = {
							crop: coverImageCrop,
							focalPoint: coverImageFocalPoint,
						}

						const coverImageGrid =
							favourite.cover_image_grid?.file &&
							generateImageUrl(favourite.cover_image_grid.file, {
								...size,
								...coverImageGridOptions,
							})

						const coverImageGrid2x =
							favourite.cover_image_grid?.file &&
							generateImageUrl(favourite.cover_image_grid.file, {
								width: size.width * 2,
								height: size.height * 2,
								...coverImageGridOptions,
							})

						const coverImage =
							favourite.cover_image?.file &&
							generateImageUrl(favourite.cover_image.file, {
								...size,
								...coverImageOptions,
							})

						const coverImage2x =
							favourite.cover_image?.file &&
							generateImageUrl(favourite.cover_image.file, {
								width: size.width * 2,
								height: size.height * 2,
								...coverImageOptions,
							})

						return (
							<CardImage
								key={index}
								loading={false}
								className="collection-list-item list-card-image"
								image={coverImage ? coverImage : coverImageGrid}
								image2x={coverImage2x ? coverImage2x : coverImageGrid2x}
								label={favourite.title}
								role="button"
								onClick={onItemClicked(favourite)}
								itemId={favourite.item_id}
								itemType={favourite.item_type}
								item={favourite}
								appMetaData={appMetaData}
								toggleFavorite={toggleFavorite}
								isFavourited={true}
							/>
						)
					})}
				</IonRow>
			</IonGrid>
			{!(favouritedItems && favouritedItems.length > 0) ? (
				<div dir={getDirMode()} className="favourite-empty-state-container">
					<i className={'fal fa-heart fa-5x'} role="img" aria-label={strings.favourite} />
					<div dir={getDirMode()} className="favourite-empty-state-block-header">
						<h2 className="favourite-empty-state-subheading">{strings.save_your_favourites}</h2>
					</div>
					<div className="block-content favourite-empty-state-block-content">{strings.save_your_favourites_desc}</div>
				</div>
			) : null}
		</>
	) : null
}

export default FavouriteGroupBlock
